import { useCallback, useState } from 'react';

import { Modal } from 'antd';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { Toast } from '@whelk/components';
import { RootState } from '@whelk/store';
import { isProd } from '@whelk/const/src/env';
import { CHAT_ID } from '@whelk/const/src/routerUrl';
import { useRouter } from '@whelk/hooks/src/useRouter';
import { isApiError } from '@whelk/services/src/common';
import { openLoginModal, trackCustomEvent } from '@whelk/utils';
import { FAIL_REASON, LOGIN_RESULT_TYPE } from '@whelk/const/src/login';
import { LocalOpenIdKey, LocalUnionIDKey } from '@whelk/const/src/token';
import { setUserInputBeforeLogin } from '@whelk/store/slices/chatListSlice';
import {
  login,
  loginFromInfo,
  setConfirmModalOpen,
  setFormTemporary,
  setShowBindModal,
  setShowLoginModal,
} from '@whelk/store/slices/userSlice';
import {
  getUnionIdOpenId,
  IS_REGISTER,
  LOGIN_TYPE,
  LoginResBody,
  postBanUserAppeal,
  postLoginByPhoneCode,
  postLoginByWx,
} from '@whelk/services/src/user';

import { checkCode, checkPhone } from '../utils';
import { PRO_TARGET_ORIGIN_LIST, WECHAT_LOGIN } from '../constants';

const useLogin = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const nav = useRouter();

  const showLoginModal = useSelector((state: RootState) => state.user.showLoginModal);
  const loginModalText = useSelector((state: RootState) => state.user.loginModalText);
  const showBindModal = useSelector((state: RootState) => state.user.showBindModal);
  const confirmModalOpen = useSelector((state: RootState) => state.user.confirmModalOpen);
  const formTemporary = useSelector((state: RootState) => state.user.formTemporary);

  const [tip, setTip] = useState<string>(WECHAT_LOGIN);
  const [checked, setChecked] = useState(false);
  const [phoneValid, setPhoneValid] = useState(false);

  const getVerifyCode = useCallback(async (event: MessageEvent) => {
    if (isProd && !PRO_TARGET_ORIGIN_LIST.includes(event?.origin)) return;
    if (event.data.hailuoWebLoginCode && typeof event.data.hailuoWebLoginCode === 'string') {
      try {
        setTip('扫码成功');
        const code = event.data.hailuoWebLoginCode;
        const model = await getUnionIdOpenId({ code });
        localStorage.setItem(LocalUnionIDKey, model.unionID);
        localStorage.setItem(LocalOpenIdKey, model.openID);
        onWXFinish();
        setTip(WECHAT_LOGIN);
      } catch (error) {
        Toast.error(t('toast_login_fail'));
        trackCustomEvent({
          id: 'login_result',
          page: 'login_popup_page',
          login_type: 'wechat',
          result_type: 2, // 失败
          fail_reason: 4, // 取消
        });
      }
    }
  }, []);

  const onClose = () => {
    localStorage.removeItem(LocalUnionIDKey);
    dispatch(setShowLoginModal(false));
    dispatch(setShowBindModal(false));
    dispatch(
      setFormTemporary({
        phone: '',
        code: '',
      }),
    );
    setChecked(false);
    dispatch(setUserInputBeforeLogin(undefined));

    trackCustomEvent({
      id: 'login_result',
      page: 'login_popup_page',
      login_type: 'mobile',
      result_type: LOGIN_RESULT_TYPE.FAIL, // 失败
      fail_reason: FAIL_REASON.CANCEL, // 取消
    });
  };

  const onSecondConfirm = () => {
    const loginType = localStorage.getItem(LocalUnionIDKey)
      ? LOGIN_TYPE.LOGINTYPETHIRDPART
      : LOGIN_TYPE.LOGINTYPEPHONE;
    handleLogin(formTemporary.phone, formTemporary.code, loginType);
    setFormTemporary({} as any);
  };

  const onSecondCancel = () => {
    dispatch(setConfirmModalOpen(false));
    openLoginModal();
  };

  /* 
      1.store用户信息
      2.关闭所有弹窗
      3.重置表单
      4.老用户重定向回首页
     */
  const loginSuccessCallback = (res: LoginResBody, loginType: LOGIN_TYPE) => {
    Toast.success(t('toast_login_success'));

    dispatch(login(res));
    dispatch(setShowLoginModal(false));
    dispatch(setShowBindModal(false));
    dispatch(setConfirmModalOpen(false));
    dispatch(
      setFormTemporary({
        phone: '',
        code: '',
      }),
    );
    setChecked(false);

    trackCustomEvent({
      id: 'login_result',
      result_type: LOGIN_RESULT_TYPE.SUCCESS, // 成功
      login_type: loginType === LOGIN_TYPE.LOGINTYPETHIRDPART ? 'wechat' : 'mobile',
      page: 'login_popup_page',
    });

    const chatID = new URLSearchParams(location.search).get(CHAT_ID);
    if (res.isRegister === IS_REGISTER.OLD_USER && chatID) {
      nav.push('/');
    }
  };

  const loginFailCallback = (err: any, loginType: LOGIN_TYPE) => {
    let fail_reason = FAIL_REASON.OTHER; // "1: 服务端异常、2: 无网、3:取消、4:其他"
    if (isApiError(err)) {
      if (err.code === 1200036) {
        formTemporary?.phone && openAppealModal(formTemporary?.phone);
      } else if (err.code === 1200044) {
        dispatch(setShowBindModal(true));
      } else {
        onSecondCancel();
        Toast.error(err.message);
      }
      fail_reason = FAIL_REASON.SERVER_ERROR;
    }
    trackCustomEvent({
      id: 'login_result',
      result_type: LOGIN_RESULT_TYPE.FAIL, // 失败
      fail_reason,
      page: 'login_popup_page',
      loginType,
    });
  };

  const handleWXLogin = async () => {
    try {
      const unionId = localStorage.getItem(LocalUnionIDKey);
      if (!unionId) return;
      const loginType = LOGIN_TYPE.LOGINTYPETHIRDPART;
      const res = await postLoginByWx({
        unionId,
        loginType,
      });
      if (!res) return;
      loginSuccessCallback(res, LOGIN_TYPE.LOGINTYPETHIRDPART);
    } catch (err) {
      loginFailCallback(err, LOGIN_TYPE.LOGINTYPETHIRDPART);
    }
  };

  const handleLogin = async (phone: string, code: string, loginType: LOGIN_TYPE) => {
    try {
      const unionId = localStorage.getItem(LocalUnionIDKey) ?? '';
      const res = await postLoginByPhoneCode({
        phone,
        code,
        unionId,
        loginType,
      });
      if (!res) return;
      loginSuccessCallback(res, loginType);
    } catch (err) {
      loginFailCallback(err, loginType);
    }
  };

  const onWXFinish = () => {
    dispatch(setShowLoginModal(false));
    handleWXLogin();
  };

  const onFinish = ({ phone, code }: loginFromInfo, loginType: LOGIN_TYPE) => {
    if (!phone) {
      Toast.info(t('login_enter_phone_number'));
      return;
    }
    if (!code) {
      Toast.warning(t('toast_login_code_required'));
      return;
    }
    if (!checkCode(code)) {
      Toast.warning(t('toast_login_code_format'));
      return;
    }
    if (!checked) {
      dispatch(
        setFormTemporary({
          phone,
          code,
        }),
      );
      dispatch(setConfirmModalOpen(true));
      dispatch(setShowLoginModal(false));
      return;
    }

    handleLogin(phone, code, loginType);
  };

  const openAppealModal = (phone: string) => {
    Modal.confirm({
      title: t('title_appeal_modal'),
      icon: null,
      width: 280,
      className: 'appeal-modal',
      content: t('text_appeal_modal'),
      cancelText: t('general_cancel'),
      okText: t('button_appeal_confirm'),
      onOk: async () => {
        try {
          await postBanUserAppeal(phone);
          Toast.success(t('toast_appeal_success'));
        } catch (err) {
          if (isApiError(err)) {
            Toast.error(err.message);
          }
        }
      },
    });
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const phone = e.target.value;
    setPhoneValid(checkPhone(phone));
  };

  return {
    checked,
    tip,
    showLoginModal,
    loginModalText,
    showBindModal,
    phoneValid,
    confirmModalOpen,
    formTemporary,
    onSecondConfirm,
    setChecked,
    onFinish,
    openAppealModal,
    onClose,
    handlePhoneChange,
    getVerifyCode,
    onSecondCancel,
  };
};

export default useLogin;
